import type { TPageTranslations } from '@crehana/i18n';

import { PAGE_KEY } from './constants';
import type { TKeys } from './types';

export const en: TPageTranslations<TKeys, typeof PAGE_KEY> = {
  [PAGE_KEY]: {
    TITLE: 'Widget',
    hoy: 'Today',
    t_registrado: 'Tracked time',
    marcar_ingreso: 'Clock in',
    iniciar_almuerzo: 'Start lunch/break',
    finalizar_almuerzo: 'End lunch/break',
    salir: 'Clock out',
    error_carga_title: 'An error occurred while loading',
    error_carga_desc: 'Click the button to try loading the content again.',
    reintentar: 'Retry',
    ingreso: 'Entry',
    inicio_almuerzo_descanso: 'Start lunch/break',
    fin_almuerzo_descanso: 'End lunch/break',
    salida: 'Exit',
    sin_registro: 'No record',
    registro_recibido: 'Record received',
    cancelar: 'Cancel',
    continuar: 'Continue',
    alerta_sin_turno_title: 'Are you sure you want to clock in?',
    alerta_sin_turno_desc:
      'You do not have a shift assigned for today; you can continue with the registration and contact your administrator for more information.',
    alerta_ingreso_no_iniciado_title: 'Are you sure you want to clock in?',
    alerta_ingreso_no_iniciado_desc:
      'You are clocking in outside your assigned schedule, as the entry time for your next shift has not started yet.',
    alerta_salida_antes_title: 'Are you sure you want to clock out?',
    alerta_salida_antes_desc:
      'Leaving before your scheduled time could affect your attendance, so we suggest reporting any issues to your administrator.',
    detalles_del_registro: 'Record details',
    dia: 'Day',
    turno_asignado: 'Assigned shift',
    evento_registrado: 'Registered event',
    hora_del_registro: 'Registration time',
    hora_estimada: 'Estimated time',
    hora_registro_tooltip:
      'The registration time may be automatically adjusted based on your company rules. Contact your administrator for more information.',
    success_ingreso: 'You have successfully clocked in.',
    success_salida: 'You have successfully clocked out.',
    success_break_start:
      'You have successfully registered the start of your lunch/break.',
    success_break_end:
      'You have successfully registered the end of your lunch/break.',
    warning_late: 'You registered your {{event}} after the scheduled time.',
    warning_early: 'You registered your {{event}} before the scheduled time.',
    error_generic:
      'An error occurred while registering attendance, please try again.',
    error_duplicate:
      'This event was already registered on another device and cannot be marked again.',
    cerrar: 'Close',
    sin_turno: 'No shift assigned',
    tiempo_registrado: 'Tracked time',
    tiempo_esperado: 'Expected time',
    registro_fuera_rango: 'Out of expected range',
    mi_asistencia: 'My attendance',
    mi_equipo: 'My team',
    ver_modulo: 'View module',
    registra_tu_asistencia: "Register your attendance for today's shift.",
    turno_completado: 'Shift completed',
    a_tiempo: 'On time',
    aceptable: 'Acceptable',
    retardo_o_falta: 'Late or absent',
    salida_anticipada: 'Early exit',
    fuera_de_horario: 'Out of schedule',
    sin_calculo: 'No calculation',

    // new keys
    date_time_display_today: 'Today',

    registered_mark_modal_title: 'Record details',
    registered_mark_modal_success_banner_message:
      'You have successfully registered your {{markName}}.',
    registered_mark_modal_delay_banner_message:
      'You registered your {{markName}} after the scheduled time.',
    registered_mark_modal_early_banner_message:
      'You registered your {{markName}} before the scheduled time.',
    registered_mark_modal_error_default:
      'An error occurred while registering attendance, please try again.',
    registered_mark_modal_error_duplicate:
      'This event was already registered on another device and cannot be marked again.',
    registered_mark_modal_row_without_shift: 'No shift assigned',
    registered_mark_modal_row_day: 'Day',
    registered_mark_modal_row_shift: 'Assigned shift',
    registered_mark_modal_row_event: 'Registered event',
    registered_mark_modal_row_time: 'Registration time',
    registered_mark_modal_row_estimated_time: 'Estimated time',
    registered_mark_modal_row_tooltip:
      'The registration time may be automatically adjusted based on your company rules. Contact your administrator for more information.',
    registered_mark_modal_row_retry: 'Retry',
    registered_mark_modal_row_cancel: 'Cancel',

    timer_circle_today: 'Today',
    timer_circle_hours_outside_schedule: 'Hours outside the expected range',
    timer_circle_t_registered: 'T. registered',
    timer_circle_time_registered: 'Time registered',
    timer_circle_time_expected: 'Time expected',

    dialog_button_continue: 'Continue',
    dialog_button_cancel: 'Cancel',

    confirm_mark_no_shift_title: 'Are you sure you want to clock in?',
    confirm_mark_no_shift_description:
      'You do not have a shift assigned for today; you can continue with the registration and contact your administrator for more information.',
    confirm_mark_early_entry_title: 'Are you sure you want to clock in?',
    confirm_mark_early_entry_description:
      'You are clocking in outside your assigned schedule, as the entry time for your next shift has not started yet.',
    confirm_mark_early_exit_title: 'Are you sure you want to clock out?',
    confirm_mark_early_exit_description:
      'Leaving before your scheduled time could affect your attendance, so we suggest reporting any issues to your administrator.',

    mark_event_entry: 'Entry',
    mark_event_break_start: 'Start of lunch/break',
    mark_event_break_end: 'End of lunch/break',
    mark_event_exit: 'Exit',

    action_buttons_mark_entry: 'Mark entry',
    action_buttons_mark_break_start: 'Start lunch/break',
    action_buttons_mark_break_end: 'End lunch/break',
    action_buttons_mark_exit: 'Mark exit',

    widget_header_tabs_personal: 'My attendance',
    widget_header_tabs_team: 'My team',
    widget_header_link_view_module: 'View module',

    widget_no_leader_title: 'My attendance',
    widget_no_leader_subtitle: "Register your attendance for today's shift.",

    team_table_column_user: 'User',
    team_table_column_shift: 'Shift',
    team_table_column_status: 'Status',
    team_table_column_scheduled_time: 'Scheduled time',

    team_table_status_without_mark: 'No record',
    team_table_status_entry_marked: 'Clocked in',
    team_table_status_break_start_marked: 'On lunch/break',
    team_table_status_break_end_marked: 'Returned from lunch/break',
    team_table_status_exit_marked: 'Clocked out',
    team_table_status_shift_break: 'Shift break',
    team_table_status_without_shift: 'No shift assigned',

    team_table_scheduled_time_not_applicable: 'Not applicable',

    team_table_error_title: 'An error occurred while loading',
    team_table_error_description:
      'Click the button to try loading the content again.',
    team_table_error_retry: 'Retry',
    team_table_empty_title: 'No team members',
    team_table_empty_description: 'There are no team members to display.',
  },
};
